import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppBar,
  Badge,
  Button,
  Card,
  Icon,
  Input,
  SegmentedControl,
  Sheet,
  Skeleton,
  TagInput,
  Textarea,
} from '@astral/ui';
import {
  PROJECT_STATUSES,
  assignSequenceToAssets,
  canCustomizePackBranding,
  formatPackTitle,
  isPublishFailureReason,
  publishFailureCopyKey,
  resolvePackBranding,
  type PackBrandingPlacement,
  readStickerData,
  type ProjectRevisionSummaryDTO,
  writeProjectCategory,
  writeStickerData,
  type Language,
  type ProjectEmojiPreset,
  type ProjectStatus,
  type StickerAsset,
  type BuiltInCategory,
  type ProjectCategoryRef,
  stickersPerPack,
  type StickerType,
} from '@astral/shared';
import { openTelegramLink } from '@astral/telegram/webapp';
import { api, ApiClientError } from '../lib/api.js';
import { membershipErrorKey } from '../lib/api-errors.js';
import { ASTRAL_CHANNEL_USERNAME } from '../config.js';
import { useCurrentRole } from '../providers/AuthProvider.js';
import { useI18n, useT, type TranslationKey } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { useUsage } from '../hooks/useUsage.js';
import { formatRelative } from '../lib/format.js';
import { StickerGrid } from '../components/StickerGrid.js';
import { ProjectPresetControl } from '../components/ProjectPresetControl.js';
import { CategoryPicker } from '../components/CategoryPicker.js';
import { useCustomCategories } from '../hooks/useCustomCategories.js';
import { PackLinkSheet } from '../components/PackLinkSheet.js';
import { EditPackNameSheet } from '../components/EditPackNameSheet.js';
import { PublishProgressSheet } from '../components/PublishProgressSheet.js';
import { PackReviewSheet } from '../components/PackReviewSheet.js';
import type { PublishJobDTO } from '@astral/shared';

type SaveState = 'idle' | 'saving' | 'saved';

/**
 * Edit a sticker project. All fields autosave (debounced); the canvas (notes +
 * sticker assets) is versioned and restorable through the existing revision
 * plumbing. Media is processed on the backend before being added.
 */
/**
 * Defensive bounds on publish polling. A job cannot outlive these, so the
 * progress dialog always resolves — even if the backend disappears mid-publish.
 */
const POLL_MAX_MS = 15 * 60 * 1000;
const POLL_MAX_CONSECUTIVE_ERRORS = 10;

/** What the pack-link sheet hands back, and what a publish retry replays. */
interface PublishInput {
  shortName: string;
  title: string;
  /** Present only for an account entitled to choose the tag's placement. */
  branding?: PackBrandingPlacement;
}

export function ProjectEditorScreen(): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { forType, usage } = useUsage();
  /*
   * Branding customisation is a PRODUCT perk — role OR active plan, never an
   * admin permission. A Premium USER qualifies and still has zero admin access.
   *
   * BOTH dimensions must be read here. Passing a hardcoded 'USER' made the
   * ADMIN/OWNER branch unreachable (the bundler folded it away entirely) and
   * left eligibility resting on `usage.plan` alone — so an OWNER saw no
   * selector whenever the usage round-trip had not resolved. The role comes
   * from the session and needs no request, so ADMIN/OWNER qualify immediately
   * and independently of `/api/usage`; a plain USER still qualifies through the
   * resolved plan, which defaults to the most restrictive value until it loads.
   *
   * Display only — the backend re-resolves role and plan itself at publish time.
   */
  const role = useCurrentRole();
  const usagePlan = usage?.plan ?? 'FREE';
  const canChooseBranding = canCustomizePackBranding(role, usagePlan);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('DRAFT');
  /*
   * The two stored columns, verbatim. Keeping the RAW value (rather than a
   * narrowed built-in) is what lets a legacy category — `other`, or anything
   * this build does not know — round-trip untouched: opening a project never
   * rewrites it, and only an explicit pick replaces it.
   */
  const [category, setCategory] = useState<string | null>(null);
  const [customCategoryId, setCustomCategoryId] = useState<string | null>(null);
  const { categories: customCategories } = useCustomCategories();
  /** Set the moment the user picks a category — see the autosave payload. */
  const categoryTouched = useRef(false);
  const [stickerType, setStickerType] = useState<StickerType>('IMAGE');
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [assets, setAssets] = useState<StickerAsset[]>([]);
  /*
   * The STORED preference, which may legitimately be absent: projects created
   * before this feature have no value, and an eligible account must keep
   * publishing untagged until it explicitly picks one. Leaving it `undefined`
   * means nothing is written to `Project.data` until the user actually chooses.
   */
  const [packBranding, setPackBranding] = useState<PackBrandingPlacement | undefined>(undefined);
  const [emojiPreset, setEmojiPreset] = useState<ProjectEmojiPreset | null>(null);

  const [historyOpen, setHistoryOpen] = useState(false);

  // Publishing / sync (real Telegram sticker set)
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<string | null>(null);
  const [busyPublish, setBusyPublish] = useState(false);
  const [packExists, setPackExists] = useState<boolean | null>(null);
  // The title the Telegram set actually carries — the only record of how a
  // published pack is branded, and what the rename sheet shows as "current".
  const [remotePackTitle, setRemotePackTitle] = useState<string | null>(null);
  const [packNameOpen, setPackNameOpen] = useState(false);
  const [reviewMode, setReviewMode] = useState<'publish' | 'sync' | null>(null);
  // Non-blocking publish flow: link chooser → background job → progress sheet.
  const [packLinkOpen, setPackLinkOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishJob, setPublishJob] = useState<PublishJobDTO | null>(null);
  const lastPublishInput = useRef<PublishInput | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mediaRefreshAt = useRef(0);

  const hydrated = useRef(false);
  const lastSaved = useRef('');
  const lastSavedData = useRef('');

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .getProject(id)
      .then((project) => {
        if (cancelled) return;
        const canvas = readStickerData(project.data);
        setTitle(project.title);
        setDescription(project.description ?? '');
        setStatus(project.status);
        setCategory(project.category);
        setCustomCategoryId(project.customCategoryId);
        setStickerType(project.stickerType);
        setTags(project.tags);
        setNotes(canvas.notes);
        setAssets(canvas.assets);
        setEmojiPreset(canvas.emojiPreset ?? null);
        setPackBranding(canvas.packBranding);
        setPublishedUrl(project.stickerSetUrl);
        setPublishedAt(project.publishedAt);
        setSyncState(project.syncState);
        // Verify the remote pack still exists (once, when opening a published project).
        if (project.stickerSetName) {
          void api
            .packStatus(project.id)
            .then((s) => {
              setPackExists(s.exists);
              setRemotePackTitle(s.title ?? null);
            })
            .catch(() => setPackExists(null));
        }
        lastSavedData.current = JSON.stringify(writeStickerData(canvas));
        lastSaved.current = JSON.stringify({
          title: project.title,
          description: project.description ?? '',
          status: project.status,
          category: project.category,
          customCategoryId: project.customCategoryId,
          stickerType: project.stickerType,
          tags: project.tags,
          data: lastSavedData.current,
        });
        hydrated.current = true;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.status === 404) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const dataString = useMemo(
    () => JSON.stringify(writeStickerData({ notes, assets, emojiPreset, packBranding })),
    [notes, assets, emojiPreset, packBranding],
  );

  /*
   * What the preview and the selector show. Derived through the SAME resolver
   * the backend uses at publish time, so the two cannot disagree: an eligible
   * account with no stored value sees NONE, everyone else sees END.
   */
  const effectiveBranding = resolvePackBranding(role, usagePlan, packBranding);

  const payload = useMemo(
    () =>
      JSON.stringify({ title, description, status, category, customCategoryId, stickerType, tags, data: dataString }),
    [title, description, status, category, customCategoryId, stickerType, tags, dataString],
  );

  useEffect(() => {
    if (!hydrated.current || !id) return;
    if (payload === lastSaved.current) return;
    if (title.trim().length === 0) return;

    setSaveState('saving');
    const handle = setTimeout(() => {
      void (async () => {
        try {
          await api.updateProject(id, {
            title: title.trim(),
            description: description.trim() || null,
            status,
            stickerType,
            tags,
            /*
             * The category is sent ONLY when the user actually changed it.
             * Otherwise a legacy value (`other`, or anything this build does
             * not recognise) would be echoed back on the first autosave and
             * rejected by the schema — or worse, quietly replaced. Not sending
             * the field leaves the stored value exactly as it was.
             */
            ...(categoryTouched.current
              ? {
                  category: category as BuiltInCategory | null,
                  customCategoryId,
                }
              : {}),
          });
          if (dataString !== lastSavedData.current) {
            const saved = await api.autosaveProject(id, {
              data: writeStickerData({ notes, assets, emojiPreset, packBranding }),
              note: 'autosave',
            });
            lastSavedData.current = dataString;
            // Backend recomputes sync state for a published pack after each save.
            if (saved.publishedAt) setSyncState(saved.syncState);
          }
          lastSaved.current = payload;
          setSaveState('saved');
        } catch {
          setSaveState('idle');
        }
      })();
    }, 900);
    return () => clearTimeout(handle);
  }, [payload, id, title, description, status, category, customCategoryId, stickerType, tags, notes, assets, emojiPreset, packBranding, dataString]);

  // Until the usage endpoint answers, assume the MOST restrictive plan rather
  // than the most permissive — an optimistic fallback would let a Free user
  // overfill a pack in the UI and only discover it when the save is rejected.
  const stickerLimit = forType(stickerType)?.stickersPerPack ?? stickersPerPack('FREE', stickerType);
  const saveLabel =
    saveState === 'saving' ? t('common.saving') : saveState === 'saved' ? t('common.saved') : '';

  /*
   * Re-mint expired media URLs.
   *
   * Signed sticker links are short-lived and are NOT stored in the project, so
   * a long editing session can outlive them. When a tile fails to load we
   * re-read the project and copy only the fresh `src` values across, keyed by
   * asset id — local edits are preserved, and because `writeStickerData` blanks
   * `src` for asset-backed stickers the refresh never registers as a change to
   * autosave. Throttled so a grid of broken tiles triggers one fetch.
   */
  const refreshMediaUrls = useCallback((): void => {
    if (!id) return;
    const now = Date.now();
    if (now - mediaRefreshAt.current < 5000) return;
    mediaRefreshAt.current = now;
    void api
      .getProject(id)
      .then((project) => {
        const fresh = new Map<string, string>();
        for (const asset of readStickerData(project.data).assets) {
          if (asset.media?.kind === 'asset') fresh.set(asset.media.assetId, asset.src);
        }
        if (fresh.size === 0) return;
        setAssets((prev) =>
          prev.map((asset) => {
            const url = asset.media?.kind === 'asset' ? fresh.get(asset.media.assetId) : undefined;
            return url && url !== asset.src ? { ...asset, src: url } : asset;
          }),
        );
      })
      .catch(() => {
        /* offline or unauthorised — the next interaction will retry */
      });
  }, [id]);

  /*
   * Poll a publish job until it reaches a TERMINAL state.
   *
   * Every exit path stops the interval. Previously any error was swallowed and
   * polling continued forever — so a job lost to a backend restart (the
   * endpoint then answering 404) left the progress dialog spinning with no way
   * out. Now a 404/401/403 is treated as terminal, transient errors are
   * tolerated only a few times in a row, and an absolute deadline backstops
   * everything.
   */
  const pollJob = (jobId: string): void => {
    if (pollRef.current) clearInterval(pollRef.current);
    const deadline = Date.now() + POLL_MAX_MS;
    let consecutiveErrors = 0;

    const stop = (): void => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
    const giveUp = (message: string): void => {
      stop();
      setPublishJob((prev) =>
        prev
          ? { ...prev, state: 'FAILED', error: message }
          : {
              id: jobId,
              projectId: id ?? '',
              state: 'FAILED',
              totalStickers: 0,
              processedStickers: 0,
              percentage: 0,
              error: message,
              errorCode: null,
              result: null,
            },
      );
    };

    const tick = async (): Promise<void> => {
      if (Date.now() > deadline) {
        giveUp(t('publish.failed'));
        return;
      }
      try {
        const job = await api.publishJob(jobId);
        consecutiveErrors = 0;
        setPublishJob(job);
        if (job.state !== 'COMPLETED' && job.state !== 'FAILED') return;

        stop();
        if (job.state === 'COMPLETED' && id) {
          const project = await api.getProject(id).catch(() => null);
          if (project) {
            setPublishedUrl(project.stickerSetUrl);
            setPublishedAt(project.publishedAt);
            setStatus(project.status);
            setSyncState(project.syncState);
          }
          setPackExists(true);
        }
      } catch (err) {
        // The job is gone or not ours — terminal, never retry.
        if (err instanceof ApiClientError && [401, 403, 404].includes(err.status)) {
          giveUp(err.status === 404 ? t('publish.failed') : err.message);
          return;
        }
        consecutiveErrors += 1;
        if (consecutiveErrors >= POLL_MAX_CONSECUTIVE_ERRORS) giveUp(t('publish.failed'));
      }
    };
    void tick();
    pollRef.current = setInterval(() => void tick(), 800);
  };

  // Start (or retry) a publish job with the given link/title/placement.
  const startPublish = async (input: PublishInput): Promise<void> => {
    if (!id) return;
    setBusyPublish(true);
    try {
      const { jobId } = await api.publishProject(id, {
        shortName: input.shortName,
        title: input.title,
        // Carried in the REQUEST so the placement cannot be staler than what the
        // user is looking at; the server still resolves entitlement itself.
        ...(input.branding ? { packBranding: input.branding } : {}),
      });
      lastPublishInput.current = input;
      setPackLinkOpen(false);
      setPublishJob(null);
      setPublishOpen(true);
      pollJob(jobId);
    } catch (err) {
      toast.show(publishErrorMessage(err, 'publish.failed'), 'error');
    } finally {
      setBusyPublish(false);
    }
  };

  const retryPublish = (): void => {
    if (lastPublishInput.current) void startPublish(lastPublishInput.current);
  };

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  /**
   * Map a publish/sync failure onto localized copy.
   *
   * Prefers the backend's structured `details.reason`; falls back to the
   * server's own safe message for the deliberate validation/conflict cases it
   * words itself, and to generic copy otherwise. A raw Telegram description can
   * no longer reach this path — the server stopped forwarding it.
   */
  const publishErrorMessage = (err: unknown, fallbackKey: TranslationKey = 'publish.syncFailed'): string => {
    if (!(err instanceof ApiClientError)) return t(fallbackKey);
    // Membership refusals are their own thing and outrank the generic mapping.
    const membership = membershipErrorKey(err);
    if (membership) return t(membership as TranslationKey, { channel: `@${ASTRAL_CHANNEL_USERNAME}` });
    const reason = err.details?.reason?.[0];
    if (isPublishFailureReason(reason)) return t(publishFailureCopyKey(reason) as TranslationKey);
    if (err.code === 'VALIDATION_ERROR') return err.message;
    return t(fallbackKey);
  };

  const sync = async (): Promise<void> => {
    if (!id) return;
    setReviewMode(null);
    setBusyPublish(true);
    setSyncState('SYNCING');
    try {
      const result = await api.syncProject(id);
      setSyncState(result.project.syncState);
      setStatus(result.project.status);
      setPackExists(true);
      toast.show(t('publish.syncDone'), 'success');
    } catch (err) {
      setSyncState('SYNC_FAILED');
      /*
       * The server's STRUCTURED reason drives the copy. Telegram's own
       * description used to be shown verbatim, so a full pack read as
       * "Telegram: Bad Request: STICKERS_TOO_MUCH" — an internal protocol
       * string that names no action and translates into no language.
       */
      toast.show(publishErrorMessage(err), 'error');
    } finally {
      setBusyPublish(false);
    }
  };

  const copyLink = (url: string): void => {
    void navigator.clipboard?.writeText(url);
    toast.show(t('common.copied'), 'success');
  };

  // Compact sync-state chip for a published pack (distinct from project status).
  const syncBadge: { tone: 'neutral' | 'success' | 'warning' | 'danger'; label: string } =
    packExists === false
      ? { tone: 'danger', label: t('publish.packMissing') }
      : syncState === 'LOCAL_CHANGES'
        ? { tone: 'warning', label: t('publish.localChanges') }
        : syncState === 'SYNCING'
          ? { tone: 'neutral', label: t('publish.updating') }
          : syncState === 'SYNC_FAILED'
            ? { tone: 'danger', label: t('publish.syncFailed') }
            : { tone: 'success', label: t('publish.synced') };

  const needsUpdate = Boolean(publishedAt) && (syncState === 'LOCAL_CHANGES' || syncState === 'SYNC_FAILED');

  return (
    <div className="app-root">
      <AppBar
        title={t('projectForm.editTitle')}
        onBack={() => navigate(-1)}
        trailing={
          <>
            {saveLabel && (
              <span className="muted" style={{ fontSize: 13, marginInlineEnd: 6 }}>
                {saveLabel}
              </span>
            )}
            <button className="icon-btn" aria-label={t('projectForm.history')} onClick={() => setHistoryOpen(true)}>
              <Icon name="history" size={18} />
            </button>
          </>
        }
      />

      <div className="screen">
        {loading ? (
          <div className="stack">
            <Skeleton height={72} radius={20} />
            <Skeleton height={54} radius={14} />
            <Skeleton height={120} radius={14} />
          </div>
        ) : notFound ? (
          <p className="muted">{t('errors.notFound')}</p>
        ) : (
          <>
            {/* Overview */}
            <Card padding="md" className="stack border-glow">
              <div className="row-between">
                <span className="screen__section-title" style={{ margin: 0 }}>
                  {t('projects.overview')}
                </span>
                <Badge tone={status === 'ACTIVE' ? 'success' : status === 'ARCHIVED' ? 'warning' : 'neutral'}>
                  {t(`projects.status.${status}` as TranslationKey)}
                </Badge>
              </div>
              <div className="overview-meta">
                <span>
                  <Icon name="folder" size={14} /> {t(`categories.${category}` as TranslationKey)}
                </span>
                <span>
                  <Icon name="sparkles" size={14} /> {t(`stickerTypes.${stickerType}` as TranslationKey)}
                </span>
                <span>
                  <Icon name="image" size={14} /> {t('stickers.count', { count: assets.length })}
                </span>
              </div>
              {publishedUrl && (
                <div className="pack-link">
                  <Badge tone={syncBadge.tone}>{syncBadge.label}</Badge>
                  <button className="pack-link__open" onClick={() => openTelegramLink(publishedUrl)}>
                    {t('publish.openPack')}
                  </button>
                  {/* Rename a published pack in place — same control set as the
                      first-publish sheet, so the project screen is unchanged. */}
                  <button className="pack-link__open" onClick={() => setPackNameOpen(true)}>
                    {t('packName.action')}
                  </button>
                  <button className="icon-btn icon-btn--sm" aria-label={t('publish.copyLink')} onClick={() => copyLink(publishedUrl)}>
                    <Icon name="copy" size={16} />
                  </button>
                </div>
              )}
            </Card>

            <Input
              label={t('projectForm.titleLabel')}
              placeholder={t('projectForm.titlePlaceholder')}
              value={title}
              maxLength={80}
              onChange={(event) => setTitle(event.target.value)}
            />
            <Textarea
              label={t('projectForm.descriptionLabel')}
              aside={t('common.optional')}
              placeholder={t('projectForm.descriptionPlaceholder')}
              value={description}
              maxLength={500}
              onChange={(event) => setDescription(event.target.value)}
            />

            {/* Category */}
            <div className="stack" style={{ gap: 8 }}>
              <span className="as-field__label">{t('projectForm.categoryLabel')}</span>
              <div className="chips">
              <CategoryPicker
                category={category}
                customCategoryId={customCategoryId}
                custom={customCategories}
                onChange={(ref: ProjectCategoryRef) => {
                  categoryTouched.current = true;
                  const columns = writeProjectCategory(ref);
                  setCategory(columns.category);
                  setCustomCategoryId(columns.customCategoryId);
                }}
              />
              </div>
            </div>

            {/* Status */}
            <div className="stack" style={{ gap: 8 }}>
              <span className="as-field__label">{t('projectForm.statusLabel')}</span>
              <SegmentedControl<ProjectStatus>
                value={status}
                onChange={setStatus}
                options={PROJECT_STATUSES.map((value) => ({
                  value,
                  label: t(`projects.status.${value}` as TranslationKey),
                }))}
              />
            </div>

            <TagInput
              label={t('projectForm.tagsLabel')}
              placeholder={t('projectForm.tagsPlaceholder')}
              value={tags}
              onChange={setTags}
            />

            {/* Default emoji preset (project-level) */}
            <ProjectPresetControl
              preset={emojiPreset}
              onChange={setEmojiPreset}
              assetsCount={assets.length}
              onApplyToAll={(emojis) => {
                const next = assignSequenceToAssets(assets, assets.map((a) => a.id), emojis);
                setAssets(next);
                if (publishedAt) setSyncState((s) => (s === 'SYNCING' ? s : 'LOCAL_CHANGES'));
                toast.show(t('stickers.emojiUpdated'), 'success');
              }}
            />

            {/* Stickers */}
            <StickerGrid
              assets={assets}
              onChange={(next) => {
                setAssets(next);
                // Optimistically flag a published pack as having local changes.
                if (publishedAt) setSyncState((s) => (s === 'SYNCING' ? s : 'LOCAL_CHANGES'));
              }}
              stickerType={stickerType}
              limit={stickerLimit}
              onMediaError={refreshMediaUrls}
              projectPreset={emojiPreset}
            />

            {/* Publish / Sync */}
            {!publishedAt ? (
              <Button
                size="lg"
                fullWidth
                loading={busyPublish}
                disabled={assets.length === 0}
                leftIcon={<Icon name="sparkles" size={18} />}
                onClick={() => setPackLinkOpen(true)}
              >
                {t('publish.action')}
              </Button>
            ) : needsUpdate ? (
              <Button
                size="lg"
                fullWidth
                loading={busyPublish}
                leftIcon={<Icon name="history" size={18} />}
                onClick={() => setReviewMode('sync')}
              >
                {t('publish.update')}
              </Button>
            ) : null}

            <Textarea
              label={t('projectForm.notesLabel')}
              aside={t('common.optional')}
              placeholder={t('projectForm.notesPlaceholder')}
              value={notes}
              rows={4}
              onChange={(event) => setNotes(event.target.value)}
            />
          </>
        )}
      </div>

      {id && (
        <PackLinkSheet
          open={packLinkOpen}
          projectId={id}
          initialTitle={title}
          branding={effectiveBranding}
          canChooseBranding={canChooseBranding}
          onBrandingChange={setPackBranding}
          busy={busyPublish}
          onConfirm={(input) => void startPublish(input)}
          onClose={() => setPackLinkOpen(false)}
        />
      )}

      {id && (
        <EditPackNameSheet
          open={packNameOpen}
          projectId={id}
          title={title}
          branding={effectiveBranding}
          canChooseBranding={canChooseBranding}
          remoteTitle={remotePackTitle}
          onSaved={(result) => {
            /*
             * Adopt the server's answer verbatim. `result.title` is the
             * UNBRANDED base it stored, and `result.branding` is the placement
             * it actually resolved — which for a lapsed account is END whatever
             * was asked for, so the editor never shows an entitlement the
             * account no longer has.
             */
            setTitle(result.title);
            setPackBranding(result.branding);
            lastSaved.current = '';
            setRemotePackTitle(formatPackTitle(result.title, result.branding));
          }}
          onClose={() => setPackNameOpen(false)}
        />
      )}

      <PublishProgressSheet
        open={publishOpen}
        job={publishJob}
        title={title}
        onRetry={retryPublish}
        onOpenPack={(url) => openTelegramLink(url)}
        onCopy={copyLink}
        onClose={() => setPublishOpen(false)}
      />

      <PackReviewSheet
        open={reviewMode !== null}
        mode={reviewMode ?? 'sync'}
        title={title}
        stickerType={stickerType}
        assets={assets}
        busy={busyPublish}
        onConfirm={() => void sync()}
        onClose={() => setReviewMode(null)}
      />

      {id && (
        <HistorySheet
          open={historyOpen}
          projectId={id}
          language={language}
          onClose={() => setHistoryOpen(false)}
          onRestore={(restored) => {
            setNotes(restored.notes);
            setAssets(restored.assets);
            setHistoryOpen(false);
            toast.show(t('common.done'), 'success');
          }}
        />
      )}
    </div>
  );
}

function HistorySheet({
  open,
  projectId,
  language,
  onClose,
  onRestore,
}: {
  open: boolean;
  projectId: string;
  language: Language;
  onClose: () => void;
  onRestore: (canvas: { notes: string; assets: StickerAsset[] }) => void;
}): JSX.Element {
  const t = useT();
  /*
   * The list is metadata only — it no longer carries every revision's payload.
   * A snapshot is fetched for the ONE revision the user chooses to restore.
   */
  const [revisions, setRevisions] = useState<ProjectRevisionSummaryDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .listRevisions(projectId)
      .then(setRevisions)
      .catch(() => setRevisions([]))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const restore = async (revisionId: string): Promise<void> => {
    setRestoringId(revisionId);
    try {
      const revision = await api.getRevision(projectId, revisionId);
      onRestore(readStickerData(revision.data));
    } catch {
      /* the sheet stays open so the user can retry */
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('projectForm.historyTitle')}>
      {loading ? (
        <div className="stack">
          <Skeleton height={48} />
          <Skeleton height={48} />
        </div>
      ) : revisions.length === 0 ? (
        <p className="muted">{t('projectForm.noHistory')}</p>
      ) : (
        <div className="stack">
          {revisions.map((rev) => (
            <div key={rev.id} className="row-between">
              <div>
                {/* Older revisions predate the recorded count; show the date alone. */}
                {rev.assetCount !== null && (
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {t('stickers.count', { count: rev.assetCount })}
                  </div>
                )}
                <div className="muted" style={{ fontSize: 12 }}>
                  {formatRelative(rev.createdAt, language)}
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                disabled={restoringId !== null}
                onClick={() => void restore(rev.id)}
              >
                {t('projectForm.restore')}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}
